import { useCallback, useEffect, useState } from 'react';
import {
  IconChecklist,
  IconDownload,
  IconRefresh,
  IconTrash,
  IconUserHeart,
} from '@tabler/icons-react';
import Btn from './ui/Btn';
import SendExportToDriveBtn from './SendExportToDriveBtn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Onglet « Briefing client » du dossier (ADR-024).
 *
 * Génère un briefing pédagogique pour le client (vouvoiement, phrases
 * courtes, pas de jargon) avant audience, en s'appuyant sur le contexte
 * dossier + les items cochés par l'avocat dans le hub Analyse IA.
 */

type Briefing = {
  id: string;
  dossier_id: string;
  title: string;
  salutation: string | null;
  sections: Array<{ heading: string; body: string }>;
  questions: string[];
  rappel_contact: string | null;
  audience_date: string | null;
  juridiction_hint: string | null;
  hint: string | null;
  model_used: string | null;
  prompt_version: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_eur: number | null;
  status: 'draft' | 'finalized' | 'archived';
  created_at: string;
  updated_at: string;
};

type Props = {
  dossierId: string;
};

export default function BriefingsTab({ dossierId }: Props) {
  const [items, setItems] = useState<Briefing[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    audience_date: '',
    juridiction_hint: '',
    hint: '',
  });

  const load = useCallback(async () => {
    try {
      const data = await api.get<Briefing[]>(`/dossiers/${dossierId}/briefings`);
      setItems(data);
      if (!activeId && data.length > 0) setActiveId(data[0]!.id);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [dossierId, activeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const body: Record<string, string> = {};
      if (draft.audience_date) body.audience_date = draft.audience_date;
      if (draft.juridiction_hint.trim()) body.juridiction_hint = draft.juridiction_hint.trim();
      if (draft.hint.trim()) body.hint = draft.hint.trim();
      const row = await api.post<Briefing & { status?: string; async?: boolean }>(
        `/dossiers/${dossierId}/briefings/generate`,
        body
      );
      if ((row as { async?: boolean }).async || (row as { status?: string }).status === 'queued') {
        toast.success('Briefing lancé en arrière-plan (30s) — la liste se rechargera dans ~45s.');
        setShowForm(false);
        setDraft({ audience_date: '', juridiction_hint: '', hint: '' });
        window.setTimeout(() => void load(), 45_000);
      } else {
        toast.success('Briefing généré');
        setShowForm(false);
        setDraft({ audience_date: '', juridiction_hint: '', hint: '' });
        setActiveId(row.id);
        await load();
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Supprimer ce briefing ?')) return;
    try {
      await api.delete(`/briefings/${id}`);
      toast.success('Briefing supprimé');
      if (activeId === id) setActiveId(null);
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec');
    }
  };

  if (loading) {
    return <div className="px-6 py-5 text-[13px] text-text-secondary">Chargement…</div>;
  }

  const active = items.find((i) => i.id === activeId) ?? items[0];

  return (
    <div className="px-6 py-5 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">Briefing client</h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Document pédagogique destiné au client (vouvoiement, langage simple) avant audience.
            Utilise les items cochés dans la Synthèse IA.
          </p>
        </div>
        <Btn
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setShowForm((v) => !v)}
          disabled={generating}
        >
          <IconUserHeart size={14} stroke={1.6} />
          {showForm ? 'Annuler' : 'Nouveau briefing'}
        </Btn>
      </header>

      {showForm && (
        <div className="rounded-md border border-rule bg-paper-elevated p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-[12px]">
              <span className="text-text-secondary">Date d'audience</span>
              <input
                type="date"
                value={draft.audience_date}
                onChange={(e) => setDraft((d) => ({ ...d, audience_date: e.target.value }))}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12.5px]"
              />
            </label>
            <label className="block text-[12px]">
              <span className="text-text-secondary">Juridiction (optionnel)</span>
              <input
                type="text"
                value={draft.juridiction_hint}
                onChange={(e) => setDraft((d) => ({ ...d, juridiction_hint: e.target.value }))}
                placeholder="ex. Tribunal correctionnel de Liège"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12.5px]"
              />
            </label>
          </div>
          <label className="block text-[12px]">
            <span className="text-text-secondary">Indication libre (optionnel)</span>
            <textarea
              value={draft.hint}
              onChange={(e) => setDraft((d) => ({ ...d, hint: e.target.value }))}
              rows={3}
              placeholder="ex. Insister sur le calme. Le client est anxieux. Rappeler la posture de contestation."
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12.5px]"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Btn
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
              disabled={generating}
            >
              Annuler
            </Btn>
            <Btn
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void onGenerate()}
              disabled={generating}
            >
              <IconRefresh size={14} stroke={1.6} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Génération…' : 'Générer (Sonnet ~30s)'}
            </Btn>
          </div>
        </div>
      )}

      {items.length === 0 && !showForm && (
        <div className="rounded-md border border-dashed border-rule-strong bg-paper-elevated p-5 text-center text-[13px] text-text-tertiary">
          <IconChecklist size={32} stroke={1.4} className="mx-auto" />
          <p className="mt-2 font-medium text-text-primary">Aucun briefing généré</p>
          <p className="mt-1 text-[12px]">
            Coche d'abord les items pertinents dans l'onglet « Synthèse IA », puis génère un
            briefing client adapté.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid gap-4 md:grid-cols-[260px_1fr]">
          <aside className="space-y-1.5">
            {items.map((b) => {
              const isActive = b.id === active?.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setActiveId(b.id)}
                  className={`w-full rounded-md border px-3 py-2 text-left text-[12.5px] transition ${
                    isActive
                      ? 'border-accent bg-accent-light'
                      : 'border-rule bg-paper-elevated hover:border-rule-strong'
                  }`}
                >
                  <div className="font-medium text-text-primary line-clamp-2">{b.title}</div>
                  <div className="mt-0.5 text-[11px] text-text-tertiary">
                    {new Date(b.created_at).toLocaleString('fr-BE', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                    {b.audience_date && (
                      <> · audience {new Date(b.audience_date).toLocaleDateString('fr-BE')}</>
                    )}
                  </div>
                </button>
              );
            })}
          </aside>

          {active && (
            <article className="rounded-md border border-rule bg-paper-elevated p-5 space-y-4">
              <header className="flex items-start justify-between gap-2 flex-wrap">
                <h2 className="font-serif text-[16px] font-medium text-text-primary">
                  {active.title}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-text-tertiary">
                    {active.cost_eur !== null && <>{active.cost_eur.toFixed(3)} € · </>}
                    {active.tokens_in?.toLocaleString('fr-BE')} in /{' '}
                    {active.tokens_out?.toLocaleString('fr-BE')} out
                  </span>
                  <Btn
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      void api
                        .downloadBlob(
                          `/briefings/${active.id}/export.docx`,
                          `${active.title || 'briefing'}.docx`
                        )
                        .catch((e) =>
                          toast.error(e instanceof ApiError ? e.message : 'Téléchargement échoué')
                        );
                    }}
                    title="Télécharger en .docx (Word)"
                  >
                    <IconDownload size={14} stroke={1.6} /> Word
                  </Btn>
                  <SendExportToDriveBtn
                    dossierId={dossierId}
                    exportPath={`/briefings/${active.id}/export.docx`}
                    defaultFilename={`${active.title || 'briefing'}.docx`}
                    defaultSubfolder="PROCEDURE"
                    variant="icon"
                  />
                  <button
                    type="button"
                    onClick={() => void onDelete(active.id)}
                    className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-cat-def"
                    title="Supprimer"
                  >
                    <IconTrash size={14} stroke={1.6} />
                  </button>
                </div>
              </header>

              {active.salutation && (
                <p className="text-[13px] leading-relaxed text-text-primary whitespace-pre-wrap">
                  {active.salutation}
                </p>
              )}

              {active.sections.map((s, idx) => (
                <section key={idx} className="space-y-1.5">
                  <h3 className="text-[13.5px] font-semibold text-text-primary">{s.heading}</h3>
                  <p className="text-[13px] leading-relaxed text-text-primary whitespace-pre-wrap">
                    {s.body}
                  </p>
                </section>
              ))}

              {active.questions.length > 0 && (
                <section className="space-y-1.5">
                  <h3 className="text-[13.5px] font-semibold text-text-primary">
                    Questions que vous pouvez vous poser
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-[13px] text-text-primary">
                    {active.questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </section>
              )}

              {active.rappel_contact && (
                <p className="rounded-md border border-rule bg-paper-card p-3 text-[12.5px] italic text-text-secondary">
                  {active.rappel_contact}
                </p>
              )}
            </article>
          )}
        </div>
      )}
    </div>
  );
}
